import { useMemo } from 'react'
import { api } from '../services/api'

// Thin wrapper around the catalog API so pages don't have to know the URL
// shapes. All methods return the JSON body the server renders; callers are
// responsible for loading/error state (or for going through the catalog
// Zustand store for the listing endpoint).
function buildQueryString(params = {}) {
  const search = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return

    if (key === 'dimension' && typeof value === 'object') {
      Object.entries(value).forEach(([dim, vals]) => {
        if (!vals) return
        const joined = Array.isArray(vals) ? vals.join(',') : String(vals)
        if (joined.length > 0) search.set(`dimension[${dim}]`, joined)
      })
      return
    }

    search.set(key, value)
  })

  const query = search.toString()
  return query ? `?${query}` : ''
}

export function useCatalog() {
  return useMemo(() => ({
    fetchCatalogEntries: (params = {}) =>
      api.get(`/api/v1/catalog${buildQueryString(params)}`),

    fetchCatalogEntry: (id) =>
      api.get(`/api/v1/catalog/${id}`),

    // Admin curation: patch an entry's editable fields. The server permits
    // :title, :summary, :detail, :status, :entry_type on catalog_entry.
    updateCatalogEntry: (id, attrs) =>
      api.patch(`/api/v1/catalog/${id}`, { catalog_entry: attrs }),

    fetchDimensions: () =>
      api.get('/api/v1/catalog/dimensions'),

    fetchDimension: (slug) =>
      api.get(`/api/v1/catalog/dimensions/${encodeURIComponent(slug)}`),

    fetchDimensionValues: (slug) =>
      api.get(`/api/v1/catalog/dimensions/${encodeURIComponent(slug)}/values`),

    // Taxonomy review: dimension values the extraction pipeline flagged.
    fetchDimensionValuesNeedingReview: () =>
      api.get('/api/v1/dimension_values/needs_review'),

    approveDimensionValue: (id) =>
      api.post(`/api/v1/dimension_values/${id}/approve`, {}),

    fetchDimensionView: (slug, params = {}) =>
      api.get(`/api/v1/catalog/views/${encodeURIComponent(slug)}${buildQueryString(params)}`),

    fetchMatrixView: (dim1, dim2, params = {}) =>
      api.get(
        `/api/v1/catalog/views/matrix/${encodeURIComponent(dim1)}/${encodeURIComponent(dim2)}${buildQueryString(params)}`
      ),
  }), [])
}
